import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { payCommissionWithBrowserWallet } from '../utils/hlUsdSend';
import './CommissionOwedPanel.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

export default function CommissionOwedPanel({ onPaid, refreshKey = 0 }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_BASE}/multi-asset/commission/owed`, {
        headers: authHeaders(),
      });
      setDetails(data);
    } catch (e) {
      setDetails(null);
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const payWithObot = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/commission/pay`,
        { method: 'obot' },
        { headers: authHeaders() }
      );
      setSuccess(data.message || 'Commission paid.');
      await load();
      onPaid?.(data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const payWithWallet = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const { data: preparePayload } = await axios.post(
        `${API_BASE}/multi-asset/commission/pay/prepare`,
        {},
        { headers: authHeaders() }
      );
      const signed = await payCommissionWithBrowserWallet({
        walletAddress: details?.walletAddress,
        preparePayload,
      });
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/commission/pay`,
        signed,
        { headers: authHeaders() }
      );
      setSuccess(data.message || 'Commission paid from your wallet.');
      await load();
      onPaid?.(data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !details) return null;
  if (!details?.commissionEnabled || !(details.owedUsd > 0)) return null;

  const amountLabel = `$${details.owedUsd.toFixed(2)}`;

  return (
    <section className="commission-owed-panel">
      <div className="commission-owed-panel-head">
        <h2>Overdue commission</h2>
        <strong className="commission-owed-amount">{amountLabel} USDC</strong>
      </div>

      <p className="commission-owed-desc">
        Pay platform commission from your Hyperliquid account. USDC is sent to the platform collection wallet
        {details.destinationMasked ? ` (${details.destinationMasked})` : ''} via Hyperliquid <code>usdSend</code>.
        {details.tradingBlocked && (
          <span className="commission-owed-blocked">
            {' '}
            Live trades are paused until this balance is cleared.
          </span>
        )}
      </p>

      <div className="commission-owed-stats">
        {details.withdrawableUsd != null && (
          <span>Withdrawable USDC: ${details.withdrawableUsd.toFixed(2)}</span>
        )}
        {details.walletMasked && <span>Linked wallet: {details.walletMasked}</span>}
      </div>

      {details.payBlockedReason && !details.canPay && (
        <p className="commission-owed-warn">{details.payBlockedReason}</p>
      )}

      {error && <p className="commission-owed-error">{error}</p>}
      {success && <p className="commission-owed-success">{success}</p>}

      <div className="commission-owed-actions">
        <button
          type="button"
          className="primary-btn"
          disabled={busy || !details.canPay || !details.obotSessionActive}
          onClick={payWithObot}
          title={
            !details.obotSessionActive
              ? 'Start oBot signing (gear icon) to pay without a browser prompt'
              : undefined
          }
        >
          {busy ? 'Processing…' : `Pay ${amountLabel} via oBot`}
        </button>
        <button
          type="button"
          className="secondary-btn"
          disabled={busy || !details.canPay}
          onClick={payWithWallet}
        >
          {busy ? 'Processing…' : `Pay ${amountLabel} with wallet`}
        </button>
        <button type="button" className="secondary-btn" disabled={busy} onClick={load}>
          Refresh
        </button>
      </div>

      {!details.obotSessionActive && details.canPay && (
        <p className="commission-owed-hint">
          oBot signing is off — use <strong>Pay with wallet</strong> (any EVM wallet) or start oBot from the gear
          icon.
        </p>
      )}
    </section>
  );
}
